/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Rxjs
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
// Models
import { Client, ClientStatusEnum, ClientTenantTypeEnum, CompanyTypesEnum, IApiResponse } from '@truckstop-tms/zuum-lib';
import { ClientSearch } from '@core/models';
// Services
import { ApiService } from '@core/services';
import { environment } from 'environments/environment';
import { MOCK_CLIENT } from '@core/mocks/mock-services';

const USE_MOCK_SERVICES = (environment as { useMockServices?: boolean }).useMockServices ?? false;

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  constructor(private http: HttpClient, private apiService: ApiService) {}

  getClientsCount(clusterId: number, testAccount = false): Observable<IApiResponse> {
    return this.http
      .get<IApiResponse>(
        `${this.apiService.superAdminApi}/clients/counts/${testAccount}${clusterId ? `?clusterId=${clusterId}` : ''}`
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  search(
    params: ClientSearch,
    includeAllPlans?: boolean
  ): Observable<IApiResponse<{ clients: Client[]; total: number }>> {
    return this.http
      .post<IApiResponse<{ clients: Client[]; total: number }>>(
        `${this.apiService.superAdminApi}/clients/search?includeAllPlans=${!!includeAllPlans}`,
        params
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getBranchesAndAgenciesForClient(mongoId: string): Observable<IApiResponse<Client[]>> {
    return this.http
      .get<IApiResponse<Client[]>>(`${this.apiService.superAdminApi}/clients/branches-agencies/${mongoId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  searchTenants(
    searchText: string,
    pageSize: number,
    tenantTypes: ClientTenantTypeEnum[]
  ): Observable<IApiResponse<Partial<Client>[]>> {
    return this.http
      .post<IApiResponse>(`${this.apiService.superAdminApi}/clients/search-tenants`, {
        searchText,
        pageSize,
        tenantTypes,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  searchSingle(companyId: number, companyType: CompanyTypesEnum): Observable<IApiResponse<Client>> {
    if (USE_MOCK_SERVICES) {
      const response = {
        result: { code: 200, message: '', success: true },
        data: {
          ...MOCK_CLIENT,
          id: companyId || MOCK_CLIENT.id,
        },
      } as IApiResponse<Client>;
      return of(response);
    }
    return this.http
      .post<IApiResponse<Client>>(`${this.apiService.superAdminApi}/clients/search-single`, { companyId, companyType })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getClientsByRootSubdomain(rootSubdomain: string, excludeTenantId?: string): Observable<IApiResponse<Client[]>> {
    return this.http
      .post<IApiResponse<Client[]>>(`${this.apiService.superAdminApi}/clients/search-by-rootsubdomain`, {
        rootSubdomain,
        tenantId: excludeTenantId,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getClientById(clientId: number, useES = false): Observable<IApiResponse<Client>> {
    const queryParams = `?useES=${useES ? 1 : 0}`;

    return this.http
      .get<IApiResponse<Client>>(`${this.apiService.superAdminApi}/clients/${clientId}${queryParams}`)
      .pipe(
        map((res) => ({ result: res.result, data: res.data })),
        catchError((error: HttpErrorResponse) => throwError(error))
      );
  }

  getClientByMongoId(esId: string): Observable<IApiResponse<Client>> {
    return this.http
      .get<IApiResponse<Client>>(`${this.apiService.superAdminApi}/clients/by-esid/${esId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateClientById(clientId: number, payload: Partial<Client>, logActivity = true): Observable<IApiResponse<Client>> {
    return this.http
      .post<IApiResponse<Client>>(
        `${this.apiService.superAdminApi}/clients/by-id/${clientId}?logActivity=${logActivity}`,
        payload
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateClientStatus(clientId: number, status: ClientStatusEnum): Observable<IApiResponse<Client>> {
    return this.http
      .put<IApiResponse<Client>>(`${this.apiService.superAdminApi}/clients/status/${clientId}`, { status })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getClientsByTenantId(tenantId: string): Observable<IApiResponse<Client[]>> {
    return this.http
      .get<IApiResponse<Client[]>>(`${this.apiService.superAdminApi}/clients/by-tenant-id/${tenantId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  generateTenantId(companyName: string): Observable<IApiResponse<string>> {
    return this.http
      .post<IApiResponse<string>>(`${this.apiService.superAdminApi}/clients/tenantId/`, { companyName })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  deleteClient(clientId: number): Observable<IApiResponse<number>> {
    return this.http
      .delete<IApiResponse<number>>(`${this.apiService.superAdminApi}/clients/${clientId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }
}
